import React, { useCallback } from 'react';
import { LevelDefinition } from 'libs/end-to-end/src/types/databases/dimensions/level-definition';
import { getTotalTestCount } from 'libs/end-to-end/src/tests-utils';
import { TestCount } from 'libs/end-to-end/src/tests-utils';
import { Treatment } from 'libs/end-to-end/src/treatment';
import { Test } from 'libs/end-to-end/src/tests-utils';
import { TestVideoStatus } from 'libs/end-to-end/src/tests-utils';
import { SUPPORT_EMAIL } from 'apps/sigma2-ui/src/constants/site-meta';
import { TestResultsPills } from 'apps/sigma2-ui/src/components/TestResultsPills';
import { IconChevronRight24pxSvg } from 'apps/sigma2-ui/src/components/svg/IconChevronRight24px';
import { PlayCircleFilled } from '@mui/icons-material';
import { S2Typography } from 'apps/sigma2-ui/src/components/ui/Typography';
import { Spacing } from 'apps/sigma2-ui/src/components/ui/Spacing';
import { FlatCard } from 'apps/sigma2-ui/src/components/ui/FlatCard';
import { useLanguage } from 'apps/sigma2-ui/src/lib/hooks/useLanguage';

export interface FocusAreaSolutionsCardProps {
  levelDefinition: LevelDefinition;
  testCountsForLevel: TestCount;
  displayName: string;
  selectedTreatments: Treatment[];
  summary: React.ReactNode;
  onLearnMoreClick: (args: { failedTests: Test[] }) => void;
  failedTests: Test[];
  treatmentsCount: number;
  onTreatmentsCtaClick: () => void;
}

const Header: React.FC<{
  levelName: string;
  displayName: string;
  selectedTreatments: Treatment[];
  testCountsForLevel: TestCount;
}> = ({ levelName, displayName, testCountsForLevel, selectedTreatments }) => {
  const language = useLanguage();

  return (
    <Spacing direction="vertical" size="xs">
      <Spacing direction="horizontal" size="xs">
        <S2Typography
          translatedContent={language.dangerousUserInput({
            value: `${displayName}:`,
          })}
          variant="headline-6-alt"
          textColorVariant="gray-600"
        />

        <S2Typography
          translatedContent={language.dangerousUserInput({ value: levelName })}
          variant="headline-6-alt"
        />
      </Spacing>

      <TestResultsPills
        totalTestsCount={getTotalTestCount(testCountsForLevel)}
        passedTestsCount={testCountsForLevel.passing}
        failedTestsCount={testCountsForLevel.failing}
        incompleteTestsCount={testCountsForLevel.incomplete}
        selectedTreatmentCount={selectedTreatments?.length || 0}
      />
    </Spacing>
  );
};

const LearnMoreAboutFailedTestsCta: React.FC<{
  onLearnMoreClick: () => void;
}> = ({ onLearnMoreClick }) => {
  const language = useLanguage();

  return (
    <div className="items-center">
      <button
        className="rounded-md transition-colors whitespace-nowrap inline-flex items-center cursor-pointer"
        type="button"
        onClick={onLearnMoreClick}
      >
        <S2Typography
          translatedContent={language.testsLearnAboutFailedTests()}
          variant="headline-7-alt"
          textColorVariant="gray-900"
        />

        <PlayCircleFilled className="ml-1.5 w-4 h-4" />
      </button>
    </div>
  );
};

const ContactUsCta = () => {
  const language = useLanguage();

  return (
    <a href={`mailto:${SUPPORT_EMAIL}`}>
      <S2Typography
        translatedContent={language.contactUs()}
        variant="headline-7-alt"
        textColorVariant="royal-600"
      />

      <IconChevronRight24pxSvg svg={{ className: 'w-2 inline-block ml-1.5' }} />
    </a>
  );
};

export const TreatmentsCta: React.FC<{
  selectedTreatmentsCount: number;
  testCountsForLevel: TestCount;
  treatmentsCount: number;
  onTreatmentsCtaClick: () => void;
}> = ({ testCountsForLevel, treatmentsCount, onTreatmentsCtaClick }) => {
  const language = useLanguage();
  const allTestsPassed =
    testCountsForLevel.passing === getTotalTestCount(testCountsForLevel);

  // No need to do anything here
  if (allTestsPassed) {
    return (
      <S2Typography
        translatedContent={language.noRecommendedSolutions()}
        variant="headline-7-alt"
        textColorVariant="gray-600"
      />
    );
  }

  const hasAvailableTreatments = treatmentsCount > 0;

  // Can't do anything here
  if (!allTestsPassed && !hasAvailableTreatments) {
    return <ContactUsCta />;
  }

  return (
    <button type="button" onClick={onTreatmentsCtaClick}>
      <S2Typography
        translatedContent={language.changeTreatmentsCta()}
        variant="headline-7-alt"
        textColorVariant="royal-600"
      />
    </button>
  );
};

export const FocusAreaSolutionsCard = ({
  levelDefinition,
  testCountsForLevel,
  displayName,
  selectedTreatments,
  summary,
  onLearnMoreClick,
  failedTests,
  treatmentsCount,
  onTreatmentsCtaClick,
}: FocusAreaSolutionsCardProps) => {
  const levelName = levelDefinition.displayName;
  const hasFailedTests = failedTests.length > 0;

  // Unavailable usually means that the organization hasn't done an upload since the failed test cta was added,
  // so we don't want to show the cta in that case.
  const shouldShowLearnMoreAboutFailedTestsCta =
    hasFailedTests &&
    !failedTests.every(
      (test) => test.videoStatus === TestVideoStatus.Values.unavailable,
    );

  const onLearnMoreClickHandler = useCallback(() => {
    onLearnMoreClick({
      failedTests,
    });
  }, [onLearnMoreClick, failedTests]);

  return (
    <FlatCard className="col-span-4 relative bg-white p-4">
      <Spacing direction="vertical" size="lg" className="pb-12">
        <Header
          selectedTreatments={selectedTreatments}
          testCountsForLevel={testCountsForLevel}
          levelName={levelName}
          displayName={displayName}
        />

        <Spacing direction="vertical" size="sm">
          {summary}

          {shouldShowLearnMoreAboutFailedTestsCta && (
            <LearnMoreAboutFailedTestsCta
              onLearnMoreClick={onLearnMoreClickHandler}
            />
          )}
        </Spacing>

        <div className="absolute bottom-4 right-4 text-right">
          <TreatmentsCta
            selectedTreatmentsCount={selectedTreatments.length}
            treatmentsCount={treatmentsCount}
            onTreatmentsCtaClick={onTreatmentsCtaClick}
            testCountsForLevel={testCountsForLevel}
          />
        </div>
      </Spacing>
    </FlatCard>
  );
};
